import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { useNavigate } from 'react-router';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    ProductTypeInput,
} from '@barboxx/admin-components';
import {
    CREATE_ALCOHOL_SUBTYPE_SCHEMA,
    ID_WIDTH,
    NAME_MIN_WIDTH,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ProductSubtype, ProductType } from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';

const columns: GridColDef<ProductSubtype.Base>[] = [
    {
        field: 'id',
        headerName: 'Id',
        width: ID_WIDTH,
    },
    {
        field: 'name',
        headerName: 'Name',
        minWidth: NAME_MIN_WIDTH,
        flex: 1,
    },
    {
        field: 'description',
        headerName: 'Description',
        minWidth: NAME_MIN_WIDTH,
        flex: 2,
    },
    {
        field: 'isAlcoholic',
        headerName: 'Is Alcoholic',
        width: 200,
        type: 'boolean',
    },
];

interface Props {
    productType: ProductType.DetailsResponse;
    refetch: () => void;
}

export const ProductSubtypeListCard = ({ productType, refetch }: Props) => {
    const { subtypes, id: productTypeId } = productType;
    const navigate = useNavigate();
    const notifications = useNotifications();
    const [open, dialogActions] = useBool();

    const formContext = useForm<ProductSubtype.CreateRequest>({
        resolver: yupResolver(CREATE_ALCOHOL_SUBTYPE_SCHEMA),
        defaultValues: {
            productTypeId: productTypeId,
        },
    });

    const mutateCreateProductSubtype = useMutation({
        mutationKey: ['productSubtype'],
        mutationFn: (data: ProductSubtype.CreateRequest) =>
            Api.Products.Subtypes.postProductSubtype(data),
    });

    const onSubmit = async (values: ProductSubtype.CreateRequest) => {
        return mutateCreateProductSubtype
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${data?.name} subtype!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                refetch();
            })
            .catch(e => {
                notifications.show('Error creating subtype!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        dialogActions.off();
    };

    const handleRowClick = (params: GridRowParams) => {
        navigate(`/products/subtypes/${params.id}`);
    };

    return (
        <>
            <DataGridCard
                title="Alcohol Subtypes List"
                HeaderComponent={() => (
                    <Button variant="contained" onClick={dialogActions.on}>
                        Add Subtype
                    </Button>
                )}
            >
                <DataGrid
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    paginationMode="client"
                    onRowClick={handleRowClick}
                    rows={subtypes}
                />
            </DataGridCard>
            <Dialog
                open={open}
                onClose={dialogActions.off}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    Create Alcohol Subtype
                </DialogTitle>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <DialogContent
                        sx={{
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <TextFieldElement label="Name" name="name" fullWidth />
                        <TextFieldElement
                            label="Description"
                            name="description"
                            fullWidth
                        />
                        <TextFieldElement
                            label="Aging Requirements"
                            name="agingRequirements"
                            fullWidth
                        />
                        <TextFieldElement
                            label="Regional Requirements"
                            name="regionalRequirements"
                            fullWidth
                        />
                        <TextFieldElement
                            label="Other Specifications"
                            name="otherSpecifications"
                            fullWidth
                        />
                        <ProductTypeInput disabled />
                        <CheckboxElement
                            name="isAlcoholic"
                            label="Is Alcoholic"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelCreate} color="inherit">
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            variant="contained"
                            loading={formContext.formState.isSubmitting}
                            type="submit"
                            disabled={formContext.formState.isSubmitting}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </FormContainer>
            </Dialog>
        </>
    );
};
